// packages/app/src/components/admin/models/ArManejoTemplatesConfig.tsx
// AGC: [AR][MANEJO] Admin editor for the Argentine manejo templates (ar_manejo_templates).
// AGC: Faithful UX rebuild — mirrors Brazil's TaskTemplatesConfig (crop selector, stats, expandable
// AGC: task list, edit modal with tabs, per-task CRUD) but keeps the AR data model unchanged
// AGC: (phase/stageAnchor/offsetDays/metadata). AR-only; Brazil's TaskTemplatesConfig is untouched.
// AGC: CRUD is local-state + auto-persist via the existing whole-crop PUT (no per-task backend routes).
import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from '../../../hooks'
import { MetricCard } from './MetricCard'
import { SectionCard } from './SectionCard'
import { getBackendUrl } from '../../../utils/backendUrl'
import { useAuth } from '../../../hooks/useAuth'
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ListTodo,
  Package,
  AlertTriangle,
  Check,
} from 'lucide-react'

// AGC: Shapes mirror the ar_manejo_templates docs + GET /ar-manejo-templates response.
interface ArTemplateInput {
  inputId: string
  inputName: string
  category?: string
  quantityPerHa: number
  unit: string
}
interface ArTemplateMeta {
  fuente?: string
  metodo?: string
  justificacion?: string
  parametros?: Record<string, unknown> | null
}
interface ArTemplateTask {
  key: string
  title: string
  phase: string
  icon?: string
  stageAnchor: string
  offsetDays: number
  inputs: ArTemplateInput[]
  metadata?: ArTemplateMeta | null
}
interface ArTemplate {
  crop: string
  tasks: ArTemplateTask[]
  sources?: string[]
  updated_at?: string
}
// AGC: AR inputs catalog row (config/ar_insumos) — used to power the insumos picker.
interface ArCatalogInput {
  id: string
  name: string
  category: string
  unit: string
  defaultQuantityPerHa?: number
  isActive?: boolean
}

// AGC: AR manejo phases (canonical values stored as-is; labels via i18n). Source: manejoGenerator.
const AR_PHASES = ['siembra', 'fertilizacion', 'aplicacion', 'monitoreo', 'cosecha'] as const
const PHASE_ICON: Record<string, string> = {
  siembra: '🌱',
  fertilizacion: '🧪',
  aplicacion: '🧴',
  monitoreo: '👁️',
  cosecha: '🚜',
}
// AGC: Canonical ORA/FAO-56 stage anchors (stored uppercase). Label keys map to admin.arManejo.stageOpt.
const AR_STAGES = ['SIEMBRA', 'DESARROLLO', 'MEDIA ESTACIÓN', 'MADURACIÓN', 'COSECHA']
const STAGE_KEY: Record<string, string> = {
  SIEMBRA: 'siembra',
  DESARROLLO: 'desarrollo',
  'MEDIA ESTACIÓN': 'mediaEstacion',
  MADURACIÓN: 'maduracion',
  COSECHA: 'cosecha',
}

export const ArManejoTemplatesConfig: React.FC = () => {
  const { t } = useTranslation()
  const { getAuthToken } = useAuth()
  // AGC: build auth headers per request with a fresh Firebase ID token (admin gate)
  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const token = await getAuthToken()
    return { Authorization: `Bearer ${token ?? ''}`, 'Content-Type': 'application/json' }
  }, [getAuthToken])
  const [templates, setTemplates] = useState<ArTemplate[]>([])
  const [catalog, setCatalog] = useState<ArCatalogInput[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [selectedCrop, setSelectedCrop] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // AGC: Modal state — editingKey null means "new task".
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [taskForm, setTaskForm] = useState<Partial<ArTemplateTask>>({})
  const [activeTab, setActiveTab] = useState<'basic' | 'inputs' | 'trace'>('basic')

  // AGC: Label helpers — tolerate values outside the canonical lists (show raw).
  const phaseLabel = (phase: string) =>
    (AR_PHASES as readonly string[]).includes(phase) ? t(`admin.arManejo.phase.${phase}`) : phase
  const stageLabel = (stage: string) =>
    STAGE_KEY[stage] ? t(`admin.arManejo.stageOpt.${STAGE_KEY[stage]}`) : stage

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const backendUrl = getBackendUrl()
      const headers = await authHeaders()
      const [tplRes, catRes] = await Promise.all([
        fetch(`${backendUrl}/api/v1/admin/config/ar-manejo-templates`, { headers }),
        fetch(`${backendUrl}/api/v1/admin/config/ar-insumos`, { headers }),
      ])
      if (tplRes.status === 401 || tplRes.status === 403 || catRes.status === 401 || catRes.status === 403) {
        const status = tplRes.status === 403 || catRes.status === 403 ? 403 : 401
        setError(t(status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return
      }
      const tplData = await tplRes.json()
      if (tplData.success && Array.isArray(tplData.data?.templates)) {
        const tpls = tplData.data.templates as ArTemplate[]
        setTemplates(tpls)
        setSelectedCrop((prev) => prev || (tpls[0]?.crop ?? null))
      } else {
        setError(t('admin.arManejo.loadError'))
      }
      const catData = await catRes.json()
      if (catData.success && Array.isArray(catData.data?.inputs)) {
        setCatalog(
          (catData.data.inputs as ArCatalogInput[]).sort((a, b) => a.name.localeCompare(b.name)),
        )
      }
    } catch {
      setError(t('admin.arManejo.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t, authHeaders])

  useEffect(() => {
    load()
  }, [load])

  // AGC: Clear success toast after 3s.
  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => setSuccess(null), 3000)
    return () => clearTimeout(timer)
  }, [success])

  const current = selectedCrop ? templates.find((tpl) => tpl.crop === selectedCrop) : null
  const tasks = current?.tasks ?? []

  const stats = {
    crops: templates.length,
    totalTasks: templates.reduce((acc, tpl) => acc + (tpl.tasks?.length || 0), 0),
    cropTasks: tasks.length,
    cropInputs: tasks.reduce((acc, task) => acc + (task.inputs?.length || 0), 0),
  }

  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  // AGC: Persist the whole crop via the existing PUT (preserves sources). Returns success boolean.
  const persistCrop = async (crop: string, nextTasks: ArTemplateTask[], sources?: string[]) => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/ar-manejo-templates/${crop}`, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify({ template: { tasks: nextTasks, sources: sources || [] }, updatedBy: 'admin' }),
      })
      if (res.status === 401 || res.status === 403) {
        setError(t(res.status === 403 ? 'admin.auth.forbidden' : 'admin.auth.notAuthenticated'))
        return false
      }
      const data = await res.json()
      if (!data.success) {
        setError(data.error || t('admin.arManejo.loadError'))
        return false
      }
      return true
    } catch {
      setError(t('admin.arManejo.loadError'))
      return false
    } finally {
      setSaving(false)
    }
  }

  const openNewTask = () => {
    setEditingKey(null)
    setTaskForm({ title: '', phase: 'aplicacion', icon: PHASE_ICON.aplicacion, stageAnchor: 'SIEMBRA', offsetDays: 0, inputs: [], metadata: {} })
    setActiveTab('basic')
    setIsModalOpen(true)
  }

  const openEditTask = (task: ArTemplateTask) => {
    setEditingKey(task.key)
    // AGC: Clone so edits don't mutate list state until persisted.
    setTaskForm({ ...task, inputs: task.inputs.map((i) => ({ ...i })), metadata: { ...(task.metadata || {}) } })
    setActiveTab('basic')
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingKey(null)
    setTaskForm({})
  }

  const handleSaveTask = async () => {
    if (!taskForm.title?.trim() || !selectedCrop || !current) {
      setError(t('admin.arManejo.fillRequired'))
      return
    }
    const phase = taskForm.phase || 'aplicacion'
    const built: ArTemplateTask = {
      key: editingKey || `ar_task_${Date.now()}`,
      title: taskForm.title.trim(),
      phase,
      icon: taskForm.icon?.trim() || PHASE_ICON[phase] || '📋',
      stageAnchor: taskForm.stageAnchor || 'SIEMBRA',
      offsetDays: taskForm.offsetDays ?? 0,
      inputs: (taskForm.inputs || []).filter((i) => i.inputId),
      metadata: taskForm.metadata || null,
    }
    const nextTasks = editingKey
      ? current.tasks.map((task) => (task.key === editingKey ? built : task))
      : [...current.tasks, built]
    const ok = await persistCrop(selectedCrop, nextTasks, current.sources)
    if (ok) {
      setTemplates((prev) => prev.map((tpl) => (tpl.crop === selectedCrop ? { ...tpl, tasks: nextTasks } : tpl)))
      setSuccess(t('admin.arManejo.saved'))
      closeModal()
    }
  }

  const handleDeleteTask = async (task: ArTemplateTask) => {
    if (!selectedCrop || !current) return
    if (!confirm(t('admin.arManejo.deleteConfirm', { title: task.title }))) return
    const nextTasks = current.tasks.filter((existing) => existing.key !== task.key)
    const ok = await persistCrop(selectedCrop, nextTasks, current.sources)
    if (ok) {
      setTemplates((prev) => prev.map((tpl) => (tpl.crop === selectedCrop ? { ...tpl, tasks: nextTasks } : tpl)))
      setSuccess(t('admin.arManejo.deleted'))
    }
  }

  // AGC: Modal input row helpers (operate on taskForm.inputs).
  const addFormInput = () =>
    setTaskForm((prev) => ({
      ...prev,
      inputs: [...(prev.inputs || []), { inputId: '', inputName: '', quantityPerHa: 1, unit: '' }],
    }))

  const updateFormInput = (index: number, patch: Partial<ArTemplateInput>) =>
    setTaskForm((prev) => ({
      ...prev,
      inputs: (prev.inputs || []).map((inp, i) => (i === index ? { ...inp, ...patch } : inp)),
    }))

  const selectFormInput = (index: number, catalogId: string) => {
    const found = catalog.find((c) => c.id === catalogId)
    if (!found) {
      updateFormInput(index, { inputId: '' })
      return
    }
    updateFormInput(index, {
      inputId: found.id,
      inputName: found.name,
      category: found.category,
      unit: found.unit,
      quantityPerHa: found.defaultQuantityPerHa ?? 1,
    })
  }

  const removeFormInput = (index: number) =>
    setTaskForm((prev) => ({ ...prev, inputs: (prev.inputs || []).filter((_, i) => i !== index) }))

  const patchMeta = (patch: Partial<ArTemplateMeta>) =>
    setTaskForm((prev) => ({ ...prev, metadata: { ...(prev.metadata || {}), ...patch } }))

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <RefreshCw className="w-4 h-4 animate-spin" /> {t('common.loading')}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ListTodo className="w-7 h-7 text-emerald-600" />
            🇦🇷 {t('admin.arManejo.title')}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mt-1">{t('admin.arManejo.note')}</p>
        </div>
      </div>

      {/* AGC: Error/Success */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto" aria-label="dismiss">
            <X className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-700 dark:text-emerald-300">{success}</span>
        </div>
      )}

      {/* AGC: Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard icon="🌾" value={stats.crops} label={t('admin.arManejo.crops')} color="#22C55E" />
        <MetricCard icon="📋" value={stats.totalTasks} label={t('admin.arManejo.totalTasks')} color="#3B82F6" />
        <MetricCard icon="✅" value={stats.cropTasks} label={t('admin.arManejo.cropTasks')} color="#8B5CF6" />
        <MetricCard icon="📦" value={stats.cropInputs} label={t('admin.arManejo.cropInputs')} color="#F59E0B" />
      </div>

      {/* AGC: Crop selector + actions */}
      <div className="flex flex-wrap gap-4 items-center">
        <select
          value={selectedCrop || ''}
          onChange={(e) => setSelectedCrop(e.target.value)}
          className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900 min-w-[200px]"
        >
          <option value="">{t('admin.arManejo.selectCrop')}</option>
          {templates.map((tpl) => (
            <option key={tpl.crop} value={tpl.crop}>
              {tpl.crop}
            </option>
          ))}
        </select>

        <button
          onClick={openNewTask}
          disabled={!selectedCrop}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {t('admin.arManejo.addTask')}
        </button>

        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 flex items-center gap-2"
          aria-label="refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* AGC: Task list */}
      <SectionCard title={`${current?.crop || ''} · ${tasks.length} ${t('admin.arManejo.tasks')}`} icon="🗒️">
        {templates.length === 0 ? (
          <p className="text-sm text-slate-400 italic">{t('admin.arManejo.noData')}</p>
        ) : tasks.length === 0 ? (
          <div className="text-center py-10 text-slate-500 dark:text-slate-400">
            <ListTodo className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{t('admin.arManejo.noTasks')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tasks.map((task) => {
              const isOpen = expanded.has(task.key)
              return (
                <div key={task.key} className="border border-slate-200 dark:border-white/5 rounded-lg">
                  {/* AGC: Row header */}
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    onClick={() => toggleExpand(task.key)}
                  >
                    <span className="text-xl">{task.icon || PHASE_ICON[task.phase] || '📋'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{task.title}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {stageLabel(task.stageAnchor)} · {t('admin.arManejo.offset')}: {task.offsetDays} ·{' '}
                        {task.inputs?.length || 0} {t('admin.arManejo.tabInputs').toLowerCase()}
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs rounded-full bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400 whitespace-nowrap">
                      {phaseLabel(task.phase)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        openEditTask(task)
                      }}
                      className="p-1 hover:bg-slate-200 dark:hover:bg-white/10 rounded"
                      aria-label="edit"
                    >
                      <Edit2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteTask(task)
                      }}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                      aria-label="delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                  </div>

                  {/* AGC: Expanded detail */}
                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-slate-100 dark:border-white/5 space-y-3">
                      {task.inputs && task.inputs.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                            <Package className="w-4 h-4" />
                            {t('admin.arManejo.inputs')}
                          </h5>
                          <div className="grid gap-2">
                            {task.inputs.map((inp, idx) => (
                              <div
                                key={inp.inputId || idx}
                                className="flex items-center gap-3 bg-slate-50 dark:bg-white/[0.02] rounded p-2 text-sm"
                              >
                                <span className="font-medium text-slate-900 dark:text-slate-100">{inp.inputName}</span>
                                <span className="text-slate-500 dark:text-slate-400">
                                  {inp.quantityPerHa} {inp.unit}/ha
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {task.metadata && (
                        <div className="text-xs text-slate-600 dark:text-slate-300 space-y-1">
                          {task.metadata.metodo && (
                            <p>
                              <span className="font-medium">{t('admin.arManejo.method')}:</span> {task.metadata.metodo}
                            </p>
                          )}
                          {task.metadata.justificacion && (
                            <p>
                              <span className="font-medium">{t('admin.arManejo.justification')}:</span>{' '}
                              {task.metadata.justificacion}
                            </p>
                          )}
                          {task.metadata.fuente && (
                            <p className="text-slate-400">
                              <span className="font-medium">{t('admin.arManejo.source')}:</span> {task.metadata.fuente}
                            </p>
                          )}
                          {task.metadata.parametros && (
                            <p className="text-slate-400">
                              <span className="font-medium">{t('admin.arManejo.params')}:</span>{' '}
                              {Object.entries(task.metadata.parametros)
                                .map(([k, v]) => `${k}: ${String(v)}`)
                                .join(' · ')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </SectionCard>

      {/* AGC: Task edit modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[90] p-4">
          <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 dark:border-white/5 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                {editingKey ? t('admin.arManejo.editTask') : t('admin.arManejo.newTask')}
              </h3>
              <button onClick={closeModal} aria-label="close">
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>

            {/* AGC: Tabs */}
            <div className="flex gap-2 px-6 pt-4 border-b border-slate-200 dark:border-white/5">
              {(['basic', 'inputs', 'trace'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 ${
                    activeTab === tab
                      ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab === 'basic'
                    ? t('admin.arManejo.tabBasic')
                    : tab === 'inputs'
                      ? t('admin.arManejo.tabInputs')
                      : t('admin.arManejo.tabTrace')}
                </button>
              ))}
            </div>

            <div className="p-6 space-y-4">
              {/* AGC: Tab — Básico */}
              {activeTab === 'basic' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('admin.arManejo.titleField')} *
                    </label>
                    <input
                      type="text"
                      value={taskForm.title || ''}
                      onChange={(e) => setTaskForm((prev) => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('admin.arManejo.phaseLabel')}
                      </label>
                      <select
                        value={taskForm.phase || 'aplicacion'}
                        onChange={(e) =>
                          setTaskForm((prev) => ({
                            ...prev,
                            phase: e.target.value,
                            icon: prev.icon && prev.icon !== PHASE_ICON[prev.phase || ''] ? prev.icon : PHASE_ICON[e.target.value],
                          }))
                        }
                        className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                      >
                        {AR_PHASES.map((p) => (
                          <option key={p} value={p}>
                            {PHASE_ICON[p]} {t(`admin.arManejo.phase.${p}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('admin.arManejo.icon')}
                      </label>
                      <input
                        type="text"
                        value={taskForm.icon || ''}
                        onChange={(e) => setTaskForm((prev) => ({ ...prev, icon: e.target.value }))}
                        className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                        placeholder="🧴"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('admin.arManejo.stage')}
                      </label>
                      <select
                        value={taskForm.stageAnchor || 'SIEMBRA'}
                        onChange={(e) => setTaskForm((prev) => ({ ...prev, stageAnchor: e.target.value }))}
                        className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                      >
                        {/* AGC: Preserve a non-canonical existing anchor as its own option. */}
                        {!AR_STAGES.includes(taskForm.stageAnchor || '') && taskForm.stageAnchor && (
                          <option value={taskForm.stageAnchor}>{taskForm.stageAnchor}</option>
                        )}
                        {AR_STAGES.map((s) => (
                          <option key={s} value={s}>
                            {stageLabel(s)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('admin.arManejo.offset')}
                      </label>
                      <input
                        type="number"
                        value={taskForm.offsetDays ?? 0}
                        onChange={(e) =>
                          setTaskForm((prev) => ({ ...prev, offsetDays: parseInt(e.target.value, 10) || 0 }))
                        }
                        className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* AGC: Tab — Insumos */}
              {activeTab === 'inputs' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                      {t('admin.arManejo.inputs')}
                    </label>
                    <button
                      type="button"
                      onClick={addFormInput}
                      className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      {t('admin.arManejo.addInput')}
                    </button>
                  </div>
                  {(taskForm.inputs || []).map((input, index) => (
                    <div key={index} className="flex gap-2 mb-2 items-center">
                      <select
                        value={input.inputId}
                        onChange={(e) => selectFormInput(index, e.target.value)}
                        className="flex-1 px-2 py-1 border border-slate-200 dark:border-white/10 rounded bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-sm"
                      >
                        <option value="">{t('admin.arManejo.selectInput')}</option>
                        {catalog.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        step="0.01"
                        value={input.quantityPerHa}
                        onChange={(e) => updateFormInput(index, { quantityPerHa: parseFloat(e.target.value) || 0 })}
                        className="w-24 px-2 py-1 border border-slate-200 dark:border-white/10 rounded bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-sm"
                      />
                      <span className="px-1 text-sm text-slate-500 dark:text-slate-400 w-16">{input.unit || '—'}/ha</span>
                      <button
                        type="button"
                        onClick={() => removeFormInput(index)}
                        className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                        aria-label="remove"
                      >
                        <X className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  ))}
                  {(taskForm.inputs || []).length === 0 && (
                    <p className="text-xs text-slate-400 italic">{t('admin.arManejo.noInputs')}</p>
                  )}
                </div>
              )}

              {/* AGC: Tab — Trazabilidad */}
              {activeTab === 'trace' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('admin.arManejo.method')}
                    </label>
                    <input
                      type="text"
                      value={taskForm.metadata?.metodo || ''}
                      onChange={(e) => patchMeta({ metodo: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('admin.arManejo.justification')}
                    </label>
                    <textarea
                      rows={2}
                      value={taskForm.metadata?.justificacion || ''}
                      onChange={(e) => patchMeta({ justificacion: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('admin.arManejo.source')}
                    </label>
                    <input
                      type="text"
                      value={taskForm.metadata?.fuente || ''}
                      onChange={(e) => patchMeta({ fuente: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-admin-bg-dark dark:text-slate-100 text-slate-900"
                    />
                  </div>
                  {taskForm.metadata?.parametros && (
                    <div className="text-xs text-slate-400">
                      <span className="font-medium">{t('admin.arManejo.params')}:</span>{' '}
                      {Object.entries(taskForm.metadata.parametros)
                        .map(([k, v]) => `${k}: ${String(v)}`)
                        .join(' · ')}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-white/5 flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5"
              >
                {t('admin.arManejo.cancel')}
              </button>
              <button
                onClick={handleSaveTask}
                disabled={saving}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingKey ? t('admin.arManejo.update') : t('admin.arManejo.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ArManejoTemplatesConfig
